import { ModelRow, ModelSearchResult } from "@mcleod/core";
import {
    ClickEvent, Component, Dialog, ImageViewerOpenButtons, ImageViewerValues, Label, Layout, OpenType, SelectionType, Tab, Table, TableRow,
    TableSelectionEvent
} from "@mcleod/components";
import { AutogenLayoutImageTable } from "./autogen/AutogenLayoutImageTable";
import { CommonDialogs } from "../CommonDialogs";
import { UploadImages } from "./UploadImages";
import { ImageRetriever } from "./ImageRetriever";

export class ImageTable extends AutogenLayoutImageTable {
    private rowType: string;
    private rowId: string;
    private imageDialog: Dialog;
    private openButtons: ImageViewerOpenButtons;

    override onLoad() {
        this.addOpenButtonsToTableTools();
    }

    private addOpenButtonsToTableTools() {
        const tooltip = "Opens the image in the specified window/tab.  Note that if the image is of a type that" +
            " cannot be previewed, it will be downloaded instead.";
        this.openButtons = new ImageViewerOpenButtons(this, () => this.getSelectedRowImageValues());
        this.openButtons.defaultButton.setProps({
            color: "primary",
            enabled: false,
            imageHeight: 22,
            imageWidth: 22,
            tooltip
        });
        this.tableImages.addCenterTool(this.openButtons.defaultButton);

        if (this.openButtons.dropdownButton != null) {
            this.openButtons.dropdownButton.setProps({
                color: "primary",
                enabled: false,
                tooltip
            });
            this.tableImages.addCenterTool(this.openButtons.dropdownButton);
        }

        this.tableImages.addSelectionListener((event: TableSelectionEvent) => {
            this.openButtons.get().forEach((button: Component) => button.enabled = event.target.selectedRowCount === 1);
        });
    }

    private async getSelectedRowImageValues(): Promise<ImageViewerValues> {
        return await this.getImageViewerValuesForRow(this.tableImages.selectedRow);
    }

    private getImagesTab(): Tab {
        let imagesTab = this["tabImages"] as Tab;
        if (imagesTab == null && this.parent instanceof Tab) {
            imagesTab = this.parent;
        }
        return imagesTab;
    }

    private getImagesTable(): Table {
        return this["tableImages"] as Table;
    }

    public populateForRow(rowType: string, rowId: string) {
        this.rowType = rowType;
        this.rowId = rowId;
        this.mainDataSource.search({ row_type: rowType, row_id: rowId })
            .then((r: ModelSearchResult) => {
                if (r.summaryData.getBoolean("is_configured")) {
                    this._updateImageTableHeader()
                }
                else {
                    if (this.getImagesTable() != null) {
                        this.getImagesTable().visible = false;
                    }
                    if (this.getImagesTab() != null) {
                        this.getImagesTab().visible = false;
                    }
                }
            })
            .catch(error => {
                if (this.getImagesTable() != null) {
                    this.getImagesTable().visible = false;
                }
                if (this.getImagesTab() != null) {
                    this.getImagesTab().visible = false;
                }
            });
    }

    private _updateImageTableHeader() {
        const imagesTab = this.getImagesTab();
        if (imagesTab == null)
            return;
        if (imagesTab.headingLabel != null)
            imagesTab.headingLabel.fillRow = false;
        if (imagesTab.heading != null) {
            if (imagesTab["countLabel"] == null) {
                imagesTab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                imagesTab.heading.insert(imagesTab["countLabel"], imagesTab.heading.components.length);
            }
            imagesTab["countLabel"].text = this.getImagesTable().rowCount;
        }
    }

    async labelDescrOnClick(event: ClickEvent) {
        const descrLabel = event.target as Label;
        try {
            descrLabel.busy = true;
            const tableRow = TableRow.getContainingTableRow(descrLabel);
            if (this.tableImages.selectedRow !== tableRow) {
                this.tableImages.selectRow(tableRow, SelectionType.SINGLE, event.domEvent);
            }
            await this.openButtons.open(OpenType.NEW_WINDOW);
        } catch (error) {
            CommonDialogs.showError(error);
        } finally {
            descrLabel.busy = false;
        }
    }

    private async getImageViewerValuesForRow(tableRow: TableRow): Promise<ImageViewerValues> {
        if (tableRow == null) {
            return null;
        }
        const id = (tableRow.data as ModelRow).get("id");
        const descrField = tableRow.findComponentsByField("descr")[0] as Label;
        const fileName = this.rowId + "_" + descrField.text;
        return await new ImageRetriever().get(id, fileName);
    }

    async buttonAddImageOnClick() {
        const layout = Layout.getLayout("common/imaging/UploadImages", {
            maxWidth: 1100,
            width: window.innerWidth * .55,
            backgroundColor: "defaultBackground",
            borderRadius: 4,
            borderWidth: 1,
            borderShadow: true,
            borderColor: "strokeSecondary"
        }) as UploadImages;
        layout.setup(this.rowType, this.rowId, null, (rowId: string) => this.imageDialog.close());
        this.imageDialog = CommonDialogs.createDialog(layout, { title: "Upload Images", okVisible: false });
        await this.imageDialog.show();
        if (layout.addedAnImage === true)
            this.populateForRow(this.rowType, this.rowId);
    }
}
